import { EntityType } from '@/components/entities/entityType';
import ErrorMessage from '@/components/errorMessage';
import { EventPayloadViewer } from '@/components/viewers';
import { useApiContext } from '@/contexts/apiContext';
import {
  DataSourceItemDto,
  DataSourceProcessingStatusDto,
  ExecutionStep,
  ExecutionStepResult,
  PipelineExecutionState,
} from '@/interfaces/pipelineExecution.interface';
import { formatRunDuration } from '@/utils';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ErrorIcon from '@mui/icons-material/Error';
import HistoryIcon from '@mui/icons-material/History';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import StorageIcon from '@mui/icons-material/Storage';
import SyncIcon from '@mui/icons-material/Sync';
import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useConfirm } from 'material-ui-confirm';
import React, { useEffect, useRef, useState } from 'react';

const formatBytes = (bytes?: number | string | null): string => {
  if (bytes === undefined || bytes === null) return '-';
  const num = typeof bytes === 'string' ? parseInt(bytes, 10) : bytes;
  if (isNaN(num) || num === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(num) / Math.log(k));
  return `${parseFloat((num / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

type Props = {
  step: ExecutionStep;
  pipelineExecutionState?: PipelineExecutionState;
  onRestartFromPoint?: (step: any, eventDataId?: string | number) => void;
  onExecuteStep?: (
    step: any,
    options?: { inputOverride?: any; reingest?: boolean; suppressEvents?: boolean },
  ) => Promise<void> | void;
  suppressEvents?: boolean;
  onToggleSuppressEvents?: (suppress: boolean) => void;
  stepExecutionResponse?: ExecutionStepResult | null;
  onClearStepExecutionResponse?: () => void;
  onChange?: () => void;
  isExecuting?: boolean;
};

const DataSourceStepItemRow: React.FC<{
  step: ExecutionStep;
  item: DataSourceItemDto;
  selectedRunId?: string | null;
  pipelineInstanceId?: string;
  onRestartFromPoint?: (step: ExecutionStep, eventDataId?: string | number) => void;
  onItemDeleted?: () => void;
  onChange?: () => void;
}> = ({ step, item, selectedRunId, pipelineInstanceId, onRestartFromPoint, onItemDeleted, onChange }) => {
  const apiContext = useApiContext();
  const confirm = useConfirm();
  const [open, setOpen] = useState(false);
  const [eventsData, setEventsData] = useState<any[]>([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [eventsFetched, setEventsFetched] = useState(false);

  // Item deletion state (Pipeline scope)
  const [deleteItemError, setDeleteItemError] = useState<string | null>(null);

  const isFailed = Boolean(item.error);

  const handleDeleteItem = async () => {
    if (!pipelineInstanceId) return;
    try {
      await confirm({
        title: 'Delete Data Source Item',
        description: `Are you sure you want to delete "${item.itemName || `Item #${item.id}`}"? This will permanently remove the item from the data source and delete all downstream agent processing statuses and generated event data across this pipeline.`,
        confirmationText: 'Delete Item',
        cancellationText: 'Cancel',
        confirmationButtonProps: { color: 'error', variant: 'contained' },
      });
      setDeleteItemError(null);
      await apiContext.deleteDataSourceInstanceItem(step.entityDefId, step.entityId, item.itemName, pipelineInstanceId);
      if (onItemDeleted) onItemDeleted();
      if (onChange) onChange();
    } catch (err: any) {
      if (err) {
        console.error('Error deleting item from pipeline:', err);
        setDeleteItemError(err?.response?.data?.message || err?.message || 'Failed to delete item from pipeline.');
      }
    }
  };

  const handleToggle = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const nextOpen = !open;
    setOpen(nextOpen);

    if (nextOpen && !eventsFetched) {
      setLoadingEvents(true);
      try {
        const events = await apiContext.getDataSourceItemEventsData(
          step.entityDefId,
          step.entityId,
          String(item.id),
          true,
          pipelineInstanceId,
        );
        setEventsData(events || []);
        setEventsFetched(true);
      } catch (err) {
        console.error('Error fetching item events data:', err);
      } finally {
        setLoadingEvents(false);
      }
    }
  };

  // Filter events by selected run if applicable
  const displayEvents = selectedRunId
    ? eventsData.filter(
        (ed) => !ed.dataSourceProcessingStatusId || String(ed.dataSourceProcessingStatusId) === String(selectedRunId),
      )
    : eventsData;

  return (
    <>
      <TableRow hover onClick={handleToggle} sx={{ cursor: 'pointer', '& > *': { borderBottom: 'unset' } }}>
        <TableCell sx={{ py: 0.75, width: 40 }}>
          <IconButton size="small" onClick={handleToggle}>
            {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ py: 0.75 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-all' }}>
            {item.itemName}
          </Typography>
          {item.parentItemName && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Parent: {item.parentItemName}
            </Typography>
          )}
          {item.groupId && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Group: {item.groupId}
            </Typography>
          )}
          {item.error && (
            <Box sx={{ mt: 0.5, maxWidth: 260 }}>
              <Tooltip title={item.error} arrow placement="bottom">
                <Typography
                  variant="caption"
                  sx={{
                    color: '#b91c1c',
                    bgcolor: '#fef2f2',
                    border: '1px solid #fecaca',
                    borderRadius: 1,
                    px: 0.75,
                    py: 0.25,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    fontWeight: 600,
                    maxWidth: '100%',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    cursor: 'help',
                  }}
                >
                  <ErrorIcon sx={{ fontSize: 13, color: '#dc2626', flexShrink: 0 }} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.error}
                  </span>
                </Typography>
              </Tooltip>
            </Box>
          )}
        </TableCell>
        <TableCell sx={{ py: 0.75 }}>
          <Chip
            label={item.mimeType || item.itemType || 'file'}
            size="small"
            variant="outlined"
            sx={{ height: 20, fontSize: '0.65rem', maxWidth: 150 }}
          />
        </TableCell>
        <TableCell sx={{ py: 0.75, fontSize: '0.75rem', color: 'text.secondary' }}>
          {formatBytes(item.itemSize)}
        </TableCell>
        <TableCell sx={{ py: 0.75, fontSize: '0.75rem', color: 'text.secondary' }}>
          {item.lastScannedDate ? new Date(item.lastScannedDate).toLocaleString() : '-'}
        </TableCell>
        <TableCell sx={{ py: 0.75 }}>
          <Tooltip title={item.error ? `Error: ${item.error}` : 'Success'}>
            <Chip
              icon={
                isFailed ? (
                  <ErrorIcon sx={{ fontSize: '14px !important' }} />
                ) : (
                  <CheckCircleIcon sx={{ fontSize: '14px !important' }} />
                )
              }
              label={isFailed ? 'Failed' : 'Success'}
              color={isFailed ? 'error' : 'success'}
              size="small"
              variant="outlined"
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          </Tooltip>
        </TableCell>
        <TableCell sx={{ py: 0.75, width: 80 }} align="right">
          <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
            {onRestartFromPoint && (
              <Tooltip title="Re-ingest this item">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRestartFromPoint(step, item.id);
                  }}
                  sx={{ p: 0.5 }}
                >
                  <PlayArrowIcon fontSize="small" color="primary" />
                </IconButton>
              </Tooltip>
            )}
            {pipelineInstanceId && (
              <Tooltip title="Delete Item from Pipeline">
                <IconButton
                  size="small"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteItem();
                  }}
                  sx={{ p: 0.5 }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7} sx={{ maxWidth: 0, width: '100%' }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box
              sx={{
                my: 1.5,
                p: 1.5,
                bgcolor: '#f8fafc',
                borderRadius: 1,
                border: '1px solid #e2e8f0',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
                overflow: 'hidden',
              }}
            >
              <Stack spacing={1.25} sx={{ width: '100%', maxWidth: '100%' }}>
                {deleteItemError && (
                  <Box sx={{ mb: 1 }}>
                    <ErrorMessage error={{ message: deleteItemError }} />
                  </Box>
                )}
                {item.error && (
                  <Box sx={{ mb: 1 }}>
                    <ErrorMessage error={{ message: `Item Ingestion Error: ${item.error}` }} />
                  </Box>
                )}

                {loadingEvents && (
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="caption" color="text.secondary">
                      Loading item events...
                    </Typography>
                  </Stack>
                )}

                {!loadingEvents && displayEvents.length > 0
                  ? displayEvents.map((eventData, idx) => {
                      const isEventError = Boolean(eventData.error);
                      const inputEvent = eventData.inputEvent;

                      return (
                        <Box
                          key={eventData.id ?? idx}
                          sx={{
                            p: 1.5,
                            bgcolor: '#ffffff',
                            borderRadius: 1,
                            border: isEventError ? '1px solid #fecaca' : '1px solid #e2e8f0',
                          }}
                        >
                          <Stack spacing={1.25}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between">
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Chip
                                  label={eventData.eventName || 'Event'}
                                  size="small"
                                  color={isEventError ? 'error' : 'primary'}
                                  variant="outlined"
                                  sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
                                />
                                {eventData.status && (
                                  <Chip
                                    label={eventData.status}
                                    size="small"
                                    color={eventData.status === 'failed' || isEventError ? 'error' : 'success'}
                                    sx={{ height: 18, fontSize: '0.65rem' }}
                                  />
                                )}
                              </Stack>
                              {eventData.dateTime && (
                                <Typography variant="caption" color="text.secondary">
                                  {new Date(eventData.dateTime).toLocaleString()}
                                </Typography>
                              )}
                            </Stack>

                            {eventData.error && <ErrorMessage error={eventData.error} />}

                            {/* Input Event Section */}
                            {inputEvent && (
                              <Box sx={{ p: 1, bgcolor: '#f1f5f9', borderRadius: 1, border: '1px solid #e2e8f0' }}>
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  justifyContent="space-between"
                                  flexWrap="wrap"
                                  gap={1}
                                >
                                  <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                                    <Typography
                                      variant="caption"
                                      sx={{ fontWeight: 700, color: '#334155', display: 'inline-block' }}
                                    >
                                      Trigger Context:
                                    </Typography>
                                    <Chip
                                      label={
                                        inputEvent.triggerType
                                          ? `Trigger: ${inputEvent.triggerType}`
                                          : 'Ingestion Trigger'
                                      }
                                      size="small"
                                      color="primary"
                                      variant="outlined"
                                      sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
                                    />
                                  </Stack>
                                </Stack>
                              </Box>
                            )}

                            {/* Output Event Payload Section */}
                            <EventPayloadViewer
                              payload={eventData.eventPayload}
                              label="Event Payload"
                              showMimeType={true}
                            />
                          </Stack>
                        </Box>
                      );
                    })
                  : !loadingEvents && (
                      <Typography variant="caption" color="text.secondary">
                        No event records found for this item.
                      </Typography>
                    )}
              </Stack>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

    </>
  );
};

// Module-level caches to preserve runs and items across step view switching
const dataSourceItemsCache = new Map<string, DataSourceItemDto[]>();
const dataSourceRunsCache = new Map<string, DataSourceProcessingStatusDto[]>();

const DataSourceStepItemsView = ({
  step,
  pipelineExecutionState,
  onRestartFromPoint,
  onExecuteStep,
  suppressEvents,
  onToggleSuppressEvents,
  stepExecutionResponse,
  onClearStepExecutionResponse,
  onChange,
  isExecuting,
}: Props) => {
  const apiContext = useApiContext();

  const dsKey = `${step.entityDefId || ''}_${step.entityId || step.id}_${pipelineExecutionState?.pipelineInstanceId || ''}`;

  const [runs, setRuns] = useState<DataSourceProcessingStatusDto[]>(() => dataSourceRunsCache.get(dsKey) || []);
  const [items, setItems] = useState<DataSourceItemDto[]>(() => dataSourceItemsCache.get(dsKey) || []);
  const [filteredItems, setFilteredItems] = useState<DataSourceItemDto[]>(() => dataSourceItemsCache.get(dsKey) || []);

  const [loadingRuns, setLoadingRuns] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // Live progress monitoring states
  const [executionStartTime, setExecutionStartTime] = useState<number | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState<number>(0);
  const [autoRefresh, setAutoRefresh] = useState<boolean>(true);
  const [isBackgroundSyncing, setIsBackgroundSyncing] = useState<boolean>(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'success' | 'failed'>('all');
  const [showRunDetails, setShowRunDetails] = useState(false);

  // Load Runs for Last Run summary
  const fetchRuns = async (showLoading = true) => {
    if (showLoading && !dataSourceRunsCache.has(dsKey)) setLoadingRuns(true);
    try {
      const processingStatuses: DataSourceProcessingStatusDto[] =
        await apiContext.getDataSourceInstanceProcessingStatuses(step.entityDefId, step.entityId);
      dataSourceRunsCache.set(dsKey, processingStatuses || []);
      setRuns(processingStatuses || []);
    } catch (err: any) {
      console.error('Error fetching data source processing statuses:', err);
    } finally {
      if (showLoading) setLoadingRuns(false);
    }
  };

  useEffect(() => {
    if (step && (step.entityDefId || step.entityId)) {
      fetchRuns(true);
    }
  }, [step?.entityDefId, step?.entityId, pipelineExecutionState?.id, stepExecutionResponse]);

  // Load All Items for data source in pipeline
  const fetchItems = async (showLoading = true) => {
    if (showLoading && !dataSourceItemsCache.has(dsKey)) setLoadingItems(true);
    setFetchError(null);
    try {
      const allItems = await apiContext.getDataSourceInstanceItems(
        step.entityDefId,
        step.entityId,
        pipelineExecutionState?.pipelineInstanceId,
      );
      dataSourceItemsCache.set(dsKey, allItems || []);
      setItems(allItems || []);
    } catch (err: any) {
      console.error('Error fetching data source items:', err);
      if (showLoading) {
        setFetchError(err?.response?.data?.message || err?.message || 'Failed to fetch data source items.');
      }
    } finally {
      if (showLoading) setLoadingItems(false);
    }
  };

  useEffect(() => {
    if (step && (step.entityDefId || step.entityId)) {
      fetchItems(true);
    }
  }, [step?.entityDefId, step?.entityId, pipelineExecutionState?.id, stepExecutionResponse]);

  // Live timer for data source execution
  useEffect(() => {
    let timer: any = null;
    if (isExecuting || isBackgroundSyncing) {
      if (!executionStartTime) {
        setExecutionStartTime(Date.now());
      }
      timer = setInterval(() => {
        setElapsedSeconds((prev) => prev + 1);
      }, 1000);
    } else {
      setExecutionStartTime(null);
      setElapsedSeconds(0);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [isExecuting, isBackgroundSyncing]);

  // Adaptive polling interval when executing
  useEffect(() => {
    let pollInterval: any = null;
    const shouldPoll = autoRefresh && (isExecuting || isBackgroundSyncing);

    if (shouldPoll) {
      pollInterval = setInterval(async () => {
        await Promise.all([fetchItems(false), fetchRuns(false)]);
      }, 2500);
    }

    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [autoRefresh, isExecuting, isBackgroundSyncing, step?.entityDefId, step?.entityId]);

  // References to track when background syncing started and initial run ID
  const syncStartTimeRef = useRef<number | null>(null);
  const syncStartRunIdRef = useRef<string | null>(null);

  // Initialize sync tracking references when background syncing begins
  useEffect(() => {
    if (isBackgroundSyncing) {
      if (syncStartTimeRef.current === null) {
        syncStartTimeRef.current = executionStartTime || Date.now();
        syncStartRunIdRef.current = runs[0]?.id || null;
      }
    } else {
      syncStartTimeRef.current = null;
      syncStartRunIdRef.current = null;
    }
  }, [isBackgroundSyncing, executionStartTime, runs]);

  // Auto-detect when data source ingestion finishes in the background
  useEffect(() => {
    if (!isBackgroundSyncing) return;

    const latest = runs[0];
    const syncStart = syncStartTimeRef.current || 0;
    const startRunId = syncStartRunIdRef.current;

    let hasFinished = false;

    if (latest) {
      if (latest.endTime) {
        // Case 1: A new run appeared (different ID) and has completed
        if (startRunId && latest.id !== startRunId) {
          hasFinished = true;
        }
        // Case 2: There was no prior run, and now a run appeared and has completed
        else if (!startRunId) {
          hasFinished = true;
        }
        // Case 3: The same run ID finished at or after our sync start (with 10s skew buffer)
        else {
          const endMs = new Date(latest.endTime).getTime();
          if (endMs >= syncStart - 10000) {
            hasFinished = true;
          }
        }
      }
    }

    // Safeguard: Check if step status in pipeline execution state reflects completion
    if (
      step?.status === 'completed' ||
      step?.status === 'partially_completed' ||
      step?.status === 'failed'
    ) {
      if (latest?.endTime || (elapsedSeconds > 2 && !isExecuting)) {
        hasFinished = true;
      }
    }

    // Safeguard: If execution is no longer active, some time elapsed, and latest run has an endTime
    if (!isExecuting && elapsedSeconds > 3 && latest?.endTime) {
      hasFinished = true;
    }

    if (hasFinished) {
      setIsBackgroundSyncing(false);
      syncStartTimeRef.current = null;
      syncStartRunIdRef.current = null;
      fetchItems(false);
      fetchRuns(false);
      if (onChange) onChange();
      if (onClearStepExecutionResponse) onClearStepExecutionResponse();
    }
  }, [
    isBackgroundSyncing,
    runs,
    step?.status,
    elapsedSeconds,
    isExecuting,
    onChange,
    onClearStepExecutionResponse,
  ]);

  // Detect background sync or execution response
  useEffect(() => {
    if (stepExecutionResponse) {
      const err = (stepExecutionResponse.error || '').toLowerCase();
      if (stepExecutionResponse.status === 'running' || err.includes('timeout') || err.includes('timed out')) {
        setIsBackgroundSyncing(true);
        setAutoRefresh(true);
      } else if (stepExecutionResponse.status === 'completed') {
        setIsBackgroundSyncing(false);
        fetchItems(false);
        fetchRuns(false);
      }
    } else {
      if (!isExecuting && step?.status !== 'running' && (step?.status as string) !== 'processing') {
        setIsBackgroundSyncing(false);
      }
    }
  }, [stepExecutionResponse, isExecuting, step?.status]);

  // Filter Items
  useEffect(() => {
    const filtered = items.filter((item) => {
      const isFailed = Boolean(item.error);
      if (statusFilter === 'success' && isFailed) return false;
      if (statusFilter === 'failed' && !isFailed) return false;

      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      return (
        item.itemName.toLowerCase().includes(q) ||
        (item.mimeType && item.mimeType.toLowerCase().includes(q)) ||
        (item.itemType && item.itemType.toLowerCase().includes(q)) ||
        (item.error && item.error.toLowerCase().includes(q))
      );
    });
    setFilteredItems(filtered);
  }, [items, searchQuery, statusFilter]);

  const latestRun = runs[0];

  if (!loadingRuns && !loadingItems && items.length === 0 && runs.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: '#f8fafc',
            borderRadius: 2,
            border: '1px dashed #cbd5e1',
            my: 1,
          }}
        >
          {fetchError && <ErrorMessage error={fetchError} />}
          <Box
            sx={{
              p: 2,
              borderRadius: '50%',
              bgcolor: '#eff6ff',
              color: '#2563eb',
              mb: 2,
              display: 'flex',
            }}
          >
            <StorageIcon sx={{ fontSize: 36 }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', mb: 0.5 }}>
            {step.entityName || 'Data Source'} has not executed yet
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 460, textAlign: 'center' }}>
            Sync this data source using the left panel action or upload documents to discover items and emit ingestion
            events.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        flexGrow: 1,
        overflow: 'hidden',
        gap: 1,
      }}
    >
      {/* Live Progress Monitoring Banner */}
      {(isExecuting || isBackgroundSyncing) && (
        <Paper
          elevation={0}
          sx={{
            p: 1.25,
            borderRadius: 1.5,
            bgcolor: isBackgroundSyncing ? '#fffbeb' : '#f0f9ff',
            border: isBackgroundSyncing ? '1px solid #fde68a' : '1px solid #bae6fd',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            flexShrink: 0,
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0, flex: 1 }}>
              <Chip
                icon={<CircularProgress size={12} color="inherit" />}
                label={isBackgroundSyncing ? 'SYNCING IN BACKGROUND' : 'SYNCING'}
                size="small"
                color={isBackgroundSyncing ? 'warning' : 'primary'}
                sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700, px: 0.25 }}
              />
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.78rem', color: '#1e293b' }} noWrap>
                {isBackgroundSyncing
                  ? 'Request timed out, but data source ingestion is continuing in the background'
                  : 'Data source synchronization is running... Discovering items and emitting events.'}
              </Typography>
            </Stack>

            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0 }}>
              {(isExecuting || isBackgroundSyncing || elapsedSeconds > 0) && (
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: 'monospace',
                    fontWeight: 700,
                    fontSize: '0.72rem',
                    color: '#475569',
                    bgcolor: 'rgba(255,255,255,0.8)',
                    px: 0.75,
                    py: 0.2,
                    borderRadius: 1,
                    border: '1px solid #e2e8f0',
                  }}
                >
                  ⏱ {Math.floor(elapsedSeconds / 60).toString().padStart(2, '0')}:{(elapsedSeconds % 60).toString().padStart(2, '0')}
                </Typography>
              )}

              <Tooltip title={autoRefresh ? 'Click to pause live polling' : 'Click to enable live auto-refresh'}>
                <Button
                  size="small"
                  variant={autoRefresh ? 'contained' : 'outlined'}
                  color={autoRefresh ? 'primary' : 'inherit'}
                  onClick={() => setAutoRefresh(!autoRefresh)}
                  startIcon={
                    <SyncIcon
                      sx={{
                        fontSize: 13,
                        animation: loadingItems || loadingRuns ? 'spin 1.5s linear infinite' : 'none',
                        '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } },
                      }}
                    />
                  }
                  sx={{
                    height: 22,
                    fontSize: '0.68rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    px: 0.75,
                    boxShadow: 'none',
                  }}
                >
                  Live Monitor: {autoRefresh ? 'ON' : 'OFF'}
                </Button>
              </Tooltip>

              <Tooltip title="Refresh items now">
                <IconButton
                  size="small"
                  onClick={() => {
                    fetchItems(false);
                    fetchRuns(false);
                  }}
                  disabled={loadingItems}
                  sx={{ p: 0.25, bgcolor: '#ffffff', border: '1px solid #cbd5e1' }}
                >
                  <RefreshIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>

              <Tooltip title="Dismiss banner">
                <IconButton
                  size="small"
                  onClick={() => {
                    setIsBackgroundSyncing(false);
                    if (onClearStepExecutionResponse) onClearStepExecutionResponse();
                  }}
                  sx={{ p: 0.25, bgcolor: '#ffffff', border: '1px solid #cbd5e1' }}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>

          <LinearProgress
            variant="indeterminate"
            color={isBackgroundSyncing ? 'warning' : 'primary'}
            sx={{ height: 6, borderRadius: 1 }}
          />
        </Paper>
      )}

      {fetchError && <ErrorMessage error={fetchError} onClose={() => setFetchError(null)} />}

      {/* Compact Top Controls & Toolbar */}
      <Paper
        variant="outlined"
        sx={{ p: 0.75, bgcolor: '#f8fafc', borderRadius: 1, flexShrink: 0, overflowY: 'hidden', overflowX: 'auto' }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          flexWrap="wrap"
          sx={{ gap: 0.75 }}
        >
          {/* Left: Total Items Chip + Last Run Summary Info */}
          <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
            <Chip
              label={`${filteredItems.length} Ingested Item${filteredItems.length === 1 ? '' : 's'}`}
              size="small"
              sx={{ height: 26, fontSize: '0.75rem', bgcolor: '#e0f2fe', color: '#0369a1', fontWeight: 600 }}
            />

            {latestRun && (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Tooltip
                  title={
                    <Box sx={{ p: 0.5, fontSize: '0.75rem' }}>
                      <div>
                        <strong>Status:</strong> {latestRun.error ? 'Failed' : 'Completed'}
                      </div>
                      {latestRun.startTime && (
                        <div>
                           <strong>Started:</strong> {new Date(latestRun.startTime).toLocaleString()}
                        </div>
                      )}
                      {latestRun.endTime && (
                        <div>
                          <strong>Finished:</strong> {new Date(latestRun.endTime).toLocaleString()}
                        </div>
                      )}
                      <div>
                        <strong>Processed:</strong> {latestRun.successItemCount ?? 0} success,{' '}
                        {latestRun.failedItemCount ?? 0} failed
                      </div>
                      {latestRun.error && (
                        <div style={{ color: '#f87171', marginTop: 4 }}>
                          <strong>Error:</strong> {latestRun.error}
                        </div>
                      )}
                    </Box>
                  }
                >
                  <Chip
                    icon={<HistoryIcon sx={{ fontSize: '13px !important' }} />}
                    label={`Last Run: ${latestRun.successItemCount ?? 0} ok${latestRun.failedItemCount ? `, ${latestRun.failedItemCount} fail` : ''}${latestRun.startTime ? ` (${new Date(latestRun.startTime).toLocaleTimeString()})` : ''}`}
                    size="small"
                    variant="outlined"
                    color={
                      latestRun.error || (latestRun.failedItemCount && !latestRun.successItemCount)
                        ? 'error'
                        : 'default'
                    }
                    sx={{ height: 26, fontSize: '0.72rem', fontWeight: 500 }}
                  />
                </Tooltip>
                <Tooltip title={showRunDetails ? 'Hide Last Run Details' : 'Show Last Run Details'}>
                  <IconButton
                    size="small"
                    onClick={() => setShowRunDetails((prev) => !prev)}
                    sx={{
                      p: 0.4,
                      border: '1px solid #e2e8f0',
                      borderRadius: 1,
                      bgcolor: showRunDetails ? '#f1f5f9' : '#fff',
                    }}
                  >
                    <InfoOutlinedIcon sx={{ fontSize: 15, color: '#475569' }} />
                  </IconButton>
                </Tooltip>
              </Stack>
            )}
          </Stack>

          {/* Right: Search & Status Filters, Live Monitor Toggle, Refresh */}
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0, ml: 'auto' }} flexWrap="wrap">
            <TextField
              size="small"
              placeholder="Search items..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                  </InputAdornment>
                ),
                sx: { height: 26, fontSize: '0.72rem', width: 140 },
              }}
            />

            <Stack direction="row" spacing={0.25}>
              <Chip
                label="All"
                size="small"
                color={statusFilter === 'all' ? 'primary' : 'default'}
                variant={statusFilter === 'all' ? 'filled' : 'outlined'}
                onClick={() => setStatusFilter('all')}
                sx={{ height: 22, fontSize: '0.68rem', cursor: 'pointer', px: 0.25 }}
              />
              <Chip
                label="Success"
                size="small"
                color={statusFilter === 'success' ? 'success' : 'default'}
                variant={statusFilter === 'success' ? 'filled' : 'outlined'}
                onClick={() => setStatusFilter('success')}
                sx={{ height: 22, fontSize: '0.68rem', cursor: 'pointer', px: 0.25 }}
              />
              <Chip
                label="Failed"
                size="small"
                color={statusFilter === 'failed' ? 'error' : 'default'}
                variant={statusFilter === 'failed' ? 'filled' : 'outlined'}
                onClick={() => setStatusFilter('failed')}
                sx={{ height: 22, fontSize: '0.68rem', cursor: 'pointer', px: 0.25 }}
              />
            </Stack>

            {!(isExecuting || isBackgroundSyncing) && (
              <>
                <Tooltip title={autoRefresh ? 'Live Auto-Refresh active (polling every 2.5s). Click to pause.' : 'Live Auto-Refresh paused. Click to enable.'}>
                  <Button
                    size="small"
                    variant={autoRefresh ? 'contained' : 'outlined'}
                    color={autoRefresh ? 'primary' : 'inherit'}
                    onClick={() => setAutoRefresh(!autoRefresh)}
                    startIcon={
                      <SyncIcon
                        sx={{
                          fontSize: 13,
                          animation: loadingItems || loadingRuns ? 'spin 1.5s linear infinite' : 'none',
                          '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } },
                        }}
                      />
                    }
                    sx={{
                      height: 24,
                      fontSize: '0.68rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      px: 0.75,
                      boxShadow: 'none',
                    }}
                  >
                    Live Monitor: {autoRefresh ? 'ON' : 'OFF'}
                  </Button>
                </Tooltip>

                <Tooltip title="Refresh items now">
                  <IconButton
                    size="small"
                    onClick={() => {
                      fetchItems(false);
                      fetchRuns(false);
                    }}
                    disabled={loadingItems}
                    sx={{ p: 0.25, bgcolor: '#ffffff', border: '1px solid #cbd5e1' }}
                  >
                    <RefreshIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Stack>
        </Stack>

        {/* Collapsible Last Run Details */}
        {latestRun && (
          <Collapse in={showRunDetails || Boolean(latestRun.error)}>
            <Box
              sx={{ mt: 0.75, p: 1, bgcolor: '#fff', borderRadius: 1, border: '1px solid #e2e8f0', overflowX: 'auto' }}
            >
              <Stack spacing={0.5}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={1}
                  sx={{ flexWrap: 'nowrap', minWidth: 'fit-content' }}
                >
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexShrink: 0 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>
                      Last Run Summary
                    </Typography>
                    <Chip
                      label={latestRun.error ? 'FAILED' : 'COMPLETED'}
                      size="small"
                      color={latestRun.error ? 'error' : 'success'}
                      sx={{ height: 16, fontSize: '0.62rem', fontWeight: 700 }}
                    />
                    <Chip
                      label={`Trigger: ${latestRun.triggerType || 'manual'}`}
                      size="small"
                      variant="outlined"
                      sx={{ height: 16, fontSize: '0.62rem' }}
                    />
                    {latestRun.id && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                        ID: <code>{latestRun.id.length > 12 ? `${latestRun.id.slice(0, 8)}...` : latestRun.id}</code>
                      </Typography>
                    )}
                  </Stack>
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexShrink: 0 }}>
                    {latestRun.startTime && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                        Start: <strong>{new Date(latestRun.startTime).toLocaleTimeString()}</strong>
                      </Typography>
                    )}
                    {latestRun.endTime && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                        • End: <strong>{new Date(latestRun.endTime).toLocaleTimeString()}</strong>
                      </Typography>
                    )}
                    {formatRunDuration(latestRun.startTime, latestRun.endTime) && (
                      <Chip
                        label={`Duration: ${formatRunDuration(latestRun.startTime, latestRun.endTime)}`}
                        size="small"
                        sx={{ height: 16, fontSize: '0.62rem', bgcolor: '#f1f5f9', fontWeight: 600 }}
                      />
                    )}
                  </Stack>
                </Stack>
                {latestRun.error && <ErrorMessage error={latestRun.error} />}
              </Stack>
            </Box>
          </Collapse>
        )}
      </Paper>

      {/* Items Table */}
      {loadingItems ? (
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ py: 4 }}>
          <CircularProgress size={24} />
          <Typography variant="body2" color="text.secondary">
            Loading items...
          </Typography>
        </Stack>
      ) : filteredItems.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', bgcolor: '#f8fafc', borderRadius: 1 }}>
          <Typography color="text.secondary" variant="body2">
            {items.length === 0
              ? 'No items found in this data source.'
              : 'No items match the current search or status filter.'}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto' }}>
          <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', width: '100%' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 36, bgcolor: '#f8fafc', py: 0.5 }} />
                <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc', py: 0.5, fontSize: '0.75rem', width: '30%' }}>
                  Item Name
                </TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc', py: 0.5, fontSize: '0.75rem', width: '18%' }}>
                  Type / MIME
                </TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc', py: 0.5, fontSize: '0.75rem', width: 75 }}>
                  Size
                </TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc', py: 0.5, fontSize: '0.75rem', width: 140 }}>
                  Last Scanned
                </TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc', py: 0.5, fontSize: '0.75rem', width: 90 }}>
                  Status
                </TableCell>
                <TableCell sx={{ width: 75, bgcolor: '#f8fafc', py: 0.5 }} align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredItems.map((item, idx) => (
                <DataSourceStepItemRow
                  key={item.id ?? idx}
                  step={step}
                  item={item}
                  pipelineInstanceId={pipelineExecutionState?.pipelineInstanceId || pipelineExecutionState?.id}
                  onRestartFromPoint={onRestartFromPoint}
                  onItemDeleted={fetchItems}
                  onChange={onChange}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default DataSourceStepItemsView;
